import React, { useEffect, useRef, useState } from 'react';
import { alpha, styled } from '@mui/material/styles';
import { red, green } from '@mui/material/colors';
import Switch from '@mui/material/Switch';
import { config as allOrgConfig } from '../../AllOrganizations/config';
import { useDispatch } from 'react-redux';
import { userActions } from '../../../../store/userSlice';
import { useFetchData } from '../../../../hooks/useFetchData';

interface SyncJobSwitchProps {
  isSyncJobEnabled: number;
  companyName: string;
  fetchLatestData: () => void;
}

const SyncJobSwitch: React.FC<SyncJobSwitchProps> = ({
  isSyncJobEnabled,
  companyName,
  fetchLatestData,
}) => {
  const [checked, setChecked] = useState<boolean>(
    isSyncJobEnabled ? true : false
  );
  const initialRender = useRef(true);
  const dispatch = useDispatch();

  const { fetch: updateSyncJob, loader } = useFetchData({
    fetchData: (payload: any) => allOrgConfig.updateCompanySyncJob(payload),
    fetchOnMount: false,
  });

  const StyledSwitch = styled(Switch)(({ theme }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: green[800],
      '&:hover': {
        backgroundColor: alpha(green[800], theme.palette.action.hoverOpacity),
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: green[800],
    },
    '& .MuiSwitch-track': {
      backgroundColor: checked ? green[800] : red[800],
    },
    '& .MuiSwitch-thumb': {
      backgroundColor: checked ? green[800] : red[800],
    },
  }));

  const submit = async () => {
    try {
      const res: any = await updateSyncJob({
        CompanyName: companyName,
        isSyncJobEnabled: checked ? 1 : 0,
      });

      if (res) {
        dispatch(
          userActions.replaceUserState({
            successMessage: {
              sc: true,
              message: `Sync job ${
                checked ? 'enabled' : 'disabled'
              } for ${companyName}`,
            },
          })
        );
        fetchLatestData();
      }
    } catch (error) {
      // Error already handled by useFetchData hook
      // Revert the switch state on error
      setChecked(!checked);
    }
  };

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
    } else {
      submit();
    }
  }, [checked]);

  // Update local state when prop changes
  useEffect(() => {
    setChecked(isSyncJobEnabled ? true : false);
  }, [isSyncJobEnabled]);

  const label = { inputProps: { 'aria-label': 'Sync Job switch' } };

  return (
    <StyledSwitch
      className="negativeMargin"
      {...label}
      checked={checked}
      onChange={() => setChecked((prevValue) => !prevValue)}
      disabled={loader}
      sx={{
        opacity: loader ? 0.5 : 1,
        cursor: loader ? 'not-allowed' : 'pointer',
        pointerEvents: loader ? 'none' : 'auto',
      }}
    />
  );
};

export default SyncJobSwitch;
